import { type FC, useState } from "react"
import { useLocation } from "react-router"
import styled from "styled-components"

import Tooltip from "@forento/shared/components/Tooltip"
import { type PurchaseableType } from "@forento/shared/models/product"
import { toPriceString } from "@forento/shared/utilities/currency"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { useAlert } from "~/contexts/AlertContext"
import { usePlatform } from "~/contexts/PlatformContext"
import { useUser } from "~/contexts/UserContext"
import { CloseIcon } from "~/themes/school/components/Icon"
import Modal, { ModalButtons, ModalText, ModalTitle } from "~/themes/school/components/Modal"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"
import { dangerColor } from "~/utilities/styles"
import trpc, { query } from "~/utilities/trpc"

import Button from "../Button"
import PayPalButtons from "./PayPalButtons"

type Props = {
	item: {
		id: number
		type: PurchaseableType
		name: string
		shortDescription: string | null
		price: { amount: number; currency: string }
	}
	onPurchase(args: { paypalOrder: { userStatus: "existing" | "assigned" | "created"; email: string } }): Promise<void>
}
const PurchaseItem: FC<Props> = ({ item, onPurchase }) => {
	const user = useUser().user
	const platform = usePlatform().platform
	const location = useLocation()
	const t = useTranslation()
	const alert = useAlert()

	const [discount, setDiscount] = useState<{
		code: string
		name: string
		discount: { type: "percent" | "amount"; value: number }
	}>()
	const [isPaypalModalOpen, setPaypalModalOpen] = useState(false)

	const createStripeOrder = query.payment.stripe.createOrder.useMutation({
		onSuccess: ({ redirectUrl }) => (window.location.href = redirectUrl),
		async onError(error) {
			console.error(error)
			await alert.show("Error", "An error occurred while creating the order.")
		},
	})

	const buyLabel =
		item.type === "event"
			? `${t("event.buyTicket")} — ${toPriceString(item.price)}`
			: `${t("general.buy")} — ${toPriceString(item.price)}`

	return (
		<>
			<Modal isOpen={isPaypalModalOpen}>
				<ModalTitle>{item.name}</ModalTitle>
				{item.shortDescription !== null && <ModalText>{item.shortDescription}</ModalText>}
				{discount ? (
					<DiscountCode>
						{discount.name} -{" "}
						{discount.discount.type === "percent"
							? `${discount.discount.value * 100}%`
							: toPriceString({ amount: discount.discount.value, currency: platform.currency })}
						<RemoveDiscountCodeButton onClick={() => setDiscount(undefined)}>
							<RemoveDiscountCodeButtonIcon />
						</RemoveDiscountCodeButton>
					</DiscountCode>
				) : (
					<DiscountCodeButton
						onClick={async () => {
							const dialog = await alert.input(
								"Discount code",
								"Enter your discount code here",
								"Discount code",
							)
							if (!dialog.result) return

							const validation = await trpc.payment.discountCode.validatePaypal.mutate(dialog.result)
							switch (validation.status) {
								case "invalid": {
									await alert.show("Invalid code", "That code is invalid.")
									break
								}
								case "expired": {
									await alert.show("Expired code", "That code has expired.")
									break
								}
								case "max-redemptions": {
									await alert.show(
										"Max redemptions reached",
										"That code has reached its maximum number of redemptions.",
									)
									break
								}
								case "valid": {
									setDiscount(validation.code)
									dialog.close()
									break
								}
								default: {
									exhaustiveGuard(validation)
								}
							}
						}}
					>
						Apply discount code
					</DiscountCodeButton>
				)}
				<ModalButtons>
					<Button variant="secondary" onClick={() => setPaypalModalOpen(false)}>
						{t("close")}
					</Button>
					<PayPalButtons onPurchase={onPurchase} item={item} discountCode={discount?.code ?? null} />
				</ModalButtons>
			</Modal>

			<Container>
				{!platform.paymentGateway?.isEnabled ? (
					<Tooltip tooltip="Payments are not enabled">
						<Button variant="primary" isDisabled>
							{buyLabel}
						</Button>
					</Tooltip>
				) : platform.paymentGateway.type === "stripe" ? (
					<Button
						variant="primary"
						onClick={() => createStripeOrder.mutate({ item })}
						isLoading={createStripeOrder.isPending}
					>
						{buyLabel}
					</Button>
				) : platform.paymentGateway.type === "paypal" ? (
					platform.paymentGateway.hasDiscountCodes ? (
						<Button variant="primary" onClick={() => setPaypalModalOpen(true)}>
							{buyLabel}
						</Button>
					) : (
						<>
							<Price>{toPriceString(item.price)}</Price>
							<PayPalButtons onPurchase={onPurchase} item={item} discountCode={null} />
						</>
					)
				) : (
					exhaustiveGuard(platform.paymentGateway)
				)}
				{user === null && (
					<Button
						variant="secondary"
						onClick={`${routes.user.signin()}?next=${encodeURIComponent(location.pathname)}`}
					>
						{t("user.signIn.title")}
					</Button>
				)}
			</Container>
		</>
	)
}

const DiscountCode = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding-left: ${24 + 8}px;
`

const RemoveDiscountCodeButton = styled(Button)`
	width: 24px;
	height: 24px;
`

const RemoveDiscountCodeButtonIcon = styled(CloseIcon)`
	color: ${dangerColor};
	width: 100%;
	height: 100%;
`

const DiscountCodeButton = styled(Button).attrs({ variant: "link" })`
	align-self: center;
`

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`

const Price = styled.div`
	width: 100%;
	color: black;
	font-size: 20px;
	font-weight: bold;
	text-align: center;
`

export default PurchaseItem
